/* ORYX App 2.0
 *
 * Created by ORYX Movement Solutions © 2022
 * ==================================================================
 *
 * Homepage.
 */

import { myFirebase } from '@/firebaseConfig/firebaseConfig';
import { FileProcessUpdate } from '@/validation/fileProcessUpdate';
import { Capacitor } from '@capacitor/core';
import {
  IonButton,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonModal,
  IonPage,
  IonProgressBar,
  IonRow,
  IonSpinner,
  IonText,
  isPlatform,
  useIonAlert,
  useIonToast,
} from '@ionic/react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { checkmarkCircleSharp } from 'ionicons/icons';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { ActiveRecordingTimer } from '@/components/ActiveRecordingTimer/ActiveRecordingTimer';
import { AnimatedSensor } from '@/components/AnimatedSensor/AnimatedSensor';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Button } from '@/components/Button';
import { CalibrateLoading } from '@/components/CalibrateLoading/CalibrateLoading';
import { CalibrationCompleted } from '@/components/CalibrationCompleted/CalibrationCompleted';
import { CalibrationCountdown } from '@/components/CalibrationCountdown/CalibrationCountdown';
import { CalibrationFailed } from '@/components/CalibrationFailed/CalibrationFailed';
import { CardHeader } from '@/components/CardHeader';
import { Card } from '@/components/Cards';
import { ChooseMeasurementModal } from '@/components/ChooseMeasurementModal/ChooseMeasurementModal';
import { RadioSelectButtonGroup } from '@/components/RadioSelectButtonGroup/RadioSelectButtonGroup';
import { RecordingItem } from '@/components/RecordingItem/RecordingItem';
import { Sensor, SensorCard, SensorStatus } from '@/components/SensorCard/SensorCard';
import { SensorFlowModal } from '@/components/SensorFlowModal/SensorFlowModal';
import { SensorPlacement } from '@/components/SensorPlacement/SensorPlacement';
import { Stepper } from '@/components/Stepper/Stepper';
import { TagModal } from '@/components/TagModal/TagModal';
import { toast } from '@/components/Toaster/Toast';

import { uuid } from '@/utils';
import { DebugOverlay } from '@/utils/DebugOverlay';

import { hubSensorService, sensorService } from '@/controllers/sensorService';

import { useActiveSessionStore } from '@/hooks/useActiveSessionStore';
import { useFirebaseTags } from '@/hooks/useFirebaseTags';
import { useHardware } from '@/hooks/useHardware/useHardware';
import { useSensors } from '@/hooks/useSensors';

import { useAuthContext } from '@/contexts/authContext';
import { useSensorsContext } from '@/contexts/SensorsContext';
import { useUserInfoContext } from '@/contexts/userInfoContext';

import { SessionScreenContextProvider } from '@/store/SessionScreenStore';

import { SessionStatus } from '@/models';

import { NativeQSenseControl } from '@/plugins/NativeQSenseControl';
import { QSenseHubControl } from '@/plugins/QSenseHubControl';
import { Assignments, KinematicModelOptions } from '@/plugins/QSenseHubControl/types';
import { USBDevice } from '@/plugins/USBDetection';

import styles from './index.module.css';

import { MovementType } from '@/types/MovementTypes';

import { EventListenerToasts } from '../../utils/EventListenerToasts';

interface AccelerometerGraphProps {
  data: Array<{ time: number; x: number; y: number; z: number }>;
}

interface QuaternionData {
  index: number;
  w: number;
  x: number;
  y: number;
  z: number;
}

interface ComponentState {
  isConnected: boolean;
  devices: USBDevice[];
  error: string | null;
}

export const SensorTestingPage = () => {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const { disconnectSensors } = useSensorsContext();
  const [showTagModal, setShowTagModal] = useState(false);
  console.log('SensorTestingPage');
  const [isPlaying, setIsPlaying] = useState(false);
  const history = useHistory();
  const platform = isPlatform('capacitor') ? 'capacitor' : 'web';
  const userId = useAuthContext().state.userState?.user.uid;
  const [presentAlert] = useIonAlert();
  const [scanning, setScanning] = useState(false);
  const [discoveredDevices, setDiscoveredDevices] = useState<string[]>([]);
  const [connectedDevices, setConnectedDevices] = useState<string[]>([]);
  const [motionLevels, setMotionLevels] = useState<{ [device: string]: number }>({});
  const [batteryLevels, setBatteryLevels] = useState<{ [device: string]: number }>({});
  const [present] = useIonToast();
  const [data, setData] = useState([]);
  const [quaternionData, setQuaternionData] = useState<QuaternionData[]>([]);
  const { userInfo } = useUserInfoContext();
  const { tags } = useFirebaseTags(userInfo.organization_id, userInfo.id);
  const { setRecordingTypes } = useActiveSessionStore();

  const { getConfig } = useHardware();
  const [config, setConfig] = useState<{
    KinematicModel: KinematicModelOptions;
    Whitelist: string[];
    Assignments: Assignments[];
    NetworkKey: number;
  } | null>(null);

  const { searchAndConnect, startCalibration } = useSensors(false);

  // TODO: Get this from the database
  const allowedDevices = [
    'FE:82:64:29:48:E9',
    'ED:9B:08:6A:C2:C7',
    'FB:8F:72:1D:63:9B',
    'F2:A3:65:5F:EC:80',
    'C6:27:4B:1F:BA:AE',
    'C3:0D:C0:66:3D:49',
    'DD:EA:9F:5B:4E:86',
    'ED:9B:08:6A:C2:C7',
    'F1:92:9B:A5:1A:BE',
  ];

  const assignments: Assignments[] = [
    { 'E0:42:09:0A:AA:EB': 'Left Pelvis' },
    { 'BE:D1:25:FA:38:D4': 'Right Pelvis' },
    { '5A:5C:B8:1E:2D:ED': 'Left Thigh' },
    { '7A:98:7E:EB:F5:E3': 'Right Thigh' },
    { '57:E2:A9:B2:4C:FC': 'Left Calf' },
    { '37:3E:15:49:F4:F4': 'Right Calf' },
    { '2C:F6:62:27:5E:E0': 'Left Foot' },
    { '4F:B6:F8:25:96:DE': 'Right Foot' },
  ];

  const whitelist = ['E0:42:09:0A:AA:EB', 'BE:D1:25:FA:38:D4'];
  const Testconfig = {
    SessionId: '0oIrwYKSkY9GKHOwq9XR',
    SessionStartTime: new Date(),
    KinematicModel: 'ORYXGO' as KinematicModelOptions, // 'ORYXGO' | 'Pitcher_RightHanded' | 'Pitcher_LeftHanded' | 'FullBody' | 'KneeStability'
    Whitelist: [
      'E0:42:09:0A:AA:EB',
      'BE:D1:25:FA:38:D4',
      '5A:5C:B8:1E:2D:ED',
      '7A:98:7E:EB:F5:E3',
      '57:E2:A9:B2:4C:FC',
      '37:3E:15:49:F4:F4',
      '2C:F6:62:27:5E:E0',
      '4F:B6:F8:25:96:DE',
    ],
    DeviceState: '0x00F84400',
    Assignments: assignments,
    ClientName: 'Marcel Tiggelman',
    UserId: userId!,
    ClientId: 'itckXz9xxjaxjeGPxrOA7BOOhGA3',
    NetworkKey: 0, // Default network key
    IdToken: userInfo.token!,
  };

  const [progress, setProgress] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const [isDataError, setIsDataError] = useState(false);

  useEffect(() => {
    // 1️⃣ define the callback
    const handleUpdate = (e: FileProcessUpdate) => {
      console.log('fileProcessUpdate event:', e);
      if (e.variant === 'loading' && typeof e.progress === 'number') {
        setProgress(Math.max(0.1, e.progress));
      }
      if (e.variant === 'success' && typeof e.progress === 'number' && e.progress === 1) {
        setProgress(1);
      }
      if (e.variant === 'error') {
        setProgress(0);
        setIsDataError(true);
      }
    };

    // 2️⃣ subscribe
    hubSensorService.on('fileProcessUpdate', handleUpdate);

    // 3️⃣ cleanup
    return () => {
      hubSensorService.off('fileProcessUpdate', handleUpdate);
      setShowComplete(false);
    };
  }, []);

  const onSyncingComplete = () => {
    setProgress(0);
    setShowComplete(true);
  };

  useEffect(() => {
    if (progress === 1) {
      onSyncingComplete();
    }
  }, [progress]);

  useEffect(() => {
    setConfig(getConfig());

    if (!Capacitor.isNativePlatform()) return;
  }, []);

  // Set mock recording types for testing
  useEffect(() => {
    setRecordingTypes([
      {
        id: 'mock-walking',
        createdBy: 'mock',
        dateCreated: new Date().toISOString(),
        icon: 'walk',
        lastUpdated: new Date().toISOString(),
        name: 'Walking',
        priority: 1,
        status: SessionStatus.Active,
      },
      {
        id: 'mock-running',
        createdBy: 'mock',
        dateCreated: new Date().toISOString(),
        icon: 'run',
        lastUpdated: new Date().toISOString(),
        name: 'Running',
        priority: 2,
        status: SessionStatus.Active,
      },
    ]);
  }, []);

  const { hubStatus } = useSensorsContext();

  const functions = getFunctions(myFirebase, 'europe-west3');
  const addReportToSession = httpsCallable(functions, 'addReportToSession');

  async function updateSession() {
    try {
      const response = await addReportToSession({
        userId: 'LpJE2KPOBFepDqUWlWAnC3sy7nu1',
        reportRef: '3EnKPDgHvvIbmrDBJrBG',
        sessionId: 'pDJWXVLNsGKhhzB4FBHr',
      });
      console.log(response.data);
    } catch (error) {
      console.error('Error adding report:', error);
    }
  }

  const testToast = () => {
    toast({
      variant: 'success',
      title: 'Test toast',
      description:
        'Segment (segmentName) in test (segment.testName) #(segment.testIndex) is missing (Int(fraction * 100))% of samples.',
    });
  };

  return (
    <>
      <SessionScreenContextProvider>
        <EventListenerToasts useHub />
        <IonPage>
          <IonContent className='ion-padding' color='light' fullscreen id='main'>
            <IonGrid className='full_height' fixed>
              <IonRow className='ion-no-padding full_height'>
                <Card style={{ width: '100%', height: '100%' }}>
                  <CardHeader title='Hub Testing' />
                  <IonCardContent>
                    <Button onClick={() => QSenseHubControl.initializePlugin(Testconfig!)}>Initialize Plugin</Button>
                    <hr />
                    <RecordingItem
                      handleDeleteClick={() => console.log('delete')}
                      handleTagClick={() => console.log('tag')}
                      recording={{
                        id: 1,
                        deleted: false,
                        time: new Date().toString(),
                        count: 1,
                        movementType: MovementType.doubleLegSquat,
                        movementTypeId: '1',
                      }}
                    />
                    <hr />
                    <Button onClick={() => QSenseHubControl.openDocumentPicker()}>Write File Header</Button>
                    <Button
                      onClick={() => {
                        QSenseHubControl.readFile();
                      }}>
                      Read File
                    </Button>
                    <hr />
                    <div className={styles.center}>
                      {isDataError ? (
                        <SensorFlowModal
                          description='An error occurred while processing the data. Please re-take the measurements.'
                          illustration='error'
                        />
                      ) : showComplete ? (
                        <SensorFlowModal description='Recordings successfully processed' illustration='success' />
                      ) : (
                        <SensorFlowModal
                          description='Sensor Hub is syncing...'
                          illustration='plugInSyncing'
                          progress={progress}
                        />
                      )}
                    </div>
                    {/* <IonProgressBar value={progress}></IonProgressBar>
                    {showComplete && <>File parsing complete!</>} */}
                    <hr />
                    <Button onClick={() => updateSession()}>Test Cloud function</Button>
                    <Button onClick={testToast}>Test Toast</Button>
                    <Button onClick={() => QSenseHubControl.connectHub()}>Connect Hub (BLE)</Button>
                    <Button onClick={() => QSenseHubControl.DisconnectHub()}>Disconnect Hub</Button>
                    <Button onClick={() => QSenseHubControl.StartStreaming()}>Start Streaming</Button>
                    <Button onClick={() => QSenseHubControl.StopStreaming()}>Stop Streaming</Button>
                    <hr />
                    <Button onClick={() => QSenseHubControl.CreateKMOG({ config: 'ORYXGO' })}>Create KMOG</Button>
                    <hr />
                    <Button onClick={() => QSenseHubControl.StartMeasurement()}>Start Measurement</Button>
                    <Button onClick={() => QSenseHubControl.StopMeasurement()}>Stop Measurement</Button>
                    <IonRow>
                      <Button onClick={() => QSenseHubControl.Annotate({ MeasurementType: 0, Count: 1 })}>
                        Annotate: 0
                      </Button>
                      <Button onClick={() => QSenseHubControl.Annotate({ MeasurementType: 1, Count: 1 })}>
                        Annotate: 1
                      </Button>
                      <Button onClick={() => QSenseHubControl.Annotate({ MeasurementType: 2, Count: 1 })}>
                        Annotate: 2
                      </Button>
                      <Button onClick={() => QSenseHubControl.Annotate({ MeasurementType: 3, Count: 1 })}>
                        Annotate: 3
                      </Button>
                    </IonRow>
                    <IonRow>
                      {/* <Button onClick={() => setShowModal(true)}>Show Data</Button> */}
                      <Button onClick={() => history.push('/OpenFilePage')}>Open File Page</Button>
                      <Button onClick={() => setShowTagModal(true)}>Show tag modal</Button>
                    </IonRow>
                  </IonCardContent>
                  {/* {state.isConnected && state.devices.length > 0 && (
                  <div>
                    <h3>Connected Devices:</h3>
                    {state.devices.map((device, index) => (
                      <div key={device.serialNumber || index}>
                        <h4>{device.name}</h4>
                        <p>Manufacturer: {device.manufacturer}</p>
                        <p>Model: {device.modelNumber}</p>
                        <p>Serial: {device.serialNumber}</p>
                      </div>
                    ))}
                  </div>
                )} */}
                </Card>
              </IonRow>
              <IonRow>
                <Card>
                  <CardHeader title='Sensor Testing' />
                  <IonCardContent className='' style={{ height: '95%' }}>
                    <IonRow className='full_height'>
                      <IonCol className='' size='4'>
                        <IonButton
                          className='primary'
                          onClick={() => {
                            console.log('Start Scan button clicked!');
                            searchAndConnect(allowedDevices);
                            setScanning(true);
                          }}>
                          {scanning ? <IonSpinner name='bubbles' /> : 'Start Scanning'}
                        </IonButton>
                        <IonButton
                          className='primary'
                          onClick={() => {
                            NativeQSenseControl.startScan({ allowedDevices: allowedDevices })
                              .then((result) => setScanning(true))
                              .catch((error) => console.error(error));
                          }}>
                          {scanning ? <IonSpinner name='bubbles' /> : 'Start Scanning (old)'}
                        </IonButton>
                        <IonButton
                          className='primary'
                          disabled={!scanning}
                          onClick={() => {
                            console.log('Stop Scan button clicked!');
                            NativeQSenseControl.stopScan()
                              .then((result) => setScanning(false))
                              .catch((error) => console.error(error));
                          }}>
                          Stop scanning
                        </IonButton>
                        <IonButton className='primary' onClick={() => NativeQSenseControl.connectDevices()}>
                          Connect Sensors
                        </IonButton>
                        <IonButton className='primary' onClick={() => NativeQSenseControl.setDataMode()}>
                          Set DataMode
                        </IonButton>
                        <IonButton className='primary' onClick={() => NativeQSenseControl.StartStreaming()}>
                          Start Streaming
                        </IonButton>
                        <IonButton className='primary' onClick={() => NativeQSenseControl.StopStreaming()}>
                          Stop Streaming
                        </IonButton>
                        <IonButton
                          className='primary'
                          onClick={() => {
                            disconnectSensors();
                            NativeQSenseControl.disConnectDevices();
                            console.log('Disconnect Sensors button clicked!');
                          }}>
                          Disconnect Sensors
                        </IonButton>
                        <IonButton
                          className='primary'
                          onClick={() => {
                            startCalibration();
                          }}>
                          Start calibration
                        </IonButton>
                      </IonCol>
                      <IonCol className='' size='8'>
                        {discoveredDevices.map((device) => (
                          <IonRow key={uuid()}>
                            <IonCol>
                              <IonText>
                                {device}{' '}
                                {connectedDevices.includes(device) ? (
                                  <IonIcon color='success' icon={checkmarkCircleSharp} />
                                ) : null}
                              </IonText>
                            </IonCol>
                            {connectedDevices.includes(device) && (
                              <>
                                <IonCol>
                                  <IonText>{batteryLevels[device] || 0}%</IonText>
                                </IonCol>
                                <IonCol>
                                  {/* <Button onClick={() => fetchData(device, 'accelerometer')}>Fetch Accelerometer</Button> */}
                                </IonCol>
                                <IonCol>
                                  <Button onClick={() => setShowSessionModal(true)}>Show data</Button>
                                </IonCol>
                                <IonProgressBar color='secondary' value={motionLevels[device] || 0}></IonProgressBar>
                              </>
                            )}
                          </IonRow>
                        ))}
                      </IonCol>
                    </IonRow>
                    <SensorPlacement
                      centerPelvis={'hidden'}
                      leftCalf={'error'}
                      leftFoot={'not_calibrated'}
                      leftPelvis={'selected'}
                      leftThigh={'not_calibrated'}
                      rightCalf={'not_calibrated'}
                      rightFoot={'not_calibrated'}
                      rightPelvis={'not_calibrated'}
                      rightThigh={'not_calibrated'}
                    />
                  </IonCardContent>
                </Card>

                <IonRow style={{ gap: 16 }}>
                  <SensorCard
                    id='SIMULATED_DEVICE_001'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={false}
                    state={SensorStatus.CALIBRATING}
                    title='Legs'
                  />
                  <SensorCard
                    id='FB:8F:72:1D:63:9B'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={false}
                    state={SensorStatus.SKELETON}
                    title='Legs'
                  />
                  <SensorCard
                    id='FB:8F:72:1D:63:9B'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={false}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                  <SensorCard
                    id='FB:8F:72:1D:63:9B'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={false}
                    state={SensorStatus.SEARCHING}
                    title='Legs'
                  />
                  <SensorCard
                    id='FB:8F:72:1D:63:9B'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.SEARCHING}
                    title='Legs'
                  />
                  <SensorCard
                    id='FE:82:64:29:48:E9'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                  <SensorCard
                    id='ED:9B:08:6A:C2:C7'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                  <SensorCard
                    id='FB:8F:72:1D:63:9B'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                  <SensorCard
                    id='F2:A3:65:5F:EC:80'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                  <SensorCard
                    id='C6:27:4B:1F:BA:AE'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                  <SensorCard
                    id='C3:0D:C0:66:3D:49'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                  <SensorCard
                    id='DD:EA:9F:5B:4E:86'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                  <SensorCard
                    id='ED:9B:08:6A:C2:C7'
                    isHubFlow={false}
                    segment='leftCalf'
                    selected={true}
                    state={SensorStatus.CONNECTED_NOT_CALIBRATED}
                    title='Legs'
                  />
                </IonRow>
              </IonRow>

              <IonRow>
                <CalibrationCompleted description='Continue to start recording' title='Calibration completed' />
              </IonRow>

              <Stepper
                currentStep={1}
                isNextDisabled={true}
                onStepChange={function (step: number): void {
                  throw new Error('Function not implemented.');
                }}
                steps={[
                  {
                    name: 'Connect sensors',
                    title: 'Connect sensors',
                    description: 'Turn on and connect the sensors to the device',
                  },
                  {
                    name: 'Reset orientation',
                    title: 'Reset orientation',
                    description:
                      'Place all sensors flat on the table pointing in the same direction before resetting their orientation',
                  },
                  {
                    name: 'Place sensors',
                    title: 'Place sensors',
                    description: 'Place the sensors on your client as shown above, and press continue',
                  },
                  {
                    name: 'Calibrate',
                    title: 'Calibrate',
                    description: 'Ask the client to stand still in an upright position and press calibrate',
                  },
                ]}
              />

              <IonRow>
                <CalibrateLoading />
              </IonRow>

              <IonRow>
                <SensorFlowModal description='Plug *in* the sensors' illustration='plugIn' />
                <SensorFlowModal
                  description='Plug *in* the sensors'
                  illustration='success'
                  primaryButton={{
                    label: 'End recording',
                    onClick: () => {
                      console.log('This should change state');
                    },
                  }}
                />
                <SensorFlowModal description='Plug *in* the sensors' illustration='unplug' />
                <SensorFlowModal description='Plug *in* the sensors' illustration='syncing' progress={0.3} />
                <SensorFlowModal
                  description='Allow up to 20 seconds for the sensors to sync.'
                  illustration='syncing'
                  primaryButton={{
                    label: 'Yes, sensors are synced',
                    onClick: () => {
                      console.log('This should change state');
                    },
                  }}
                  secondaryButton={{
                    label: 'Reconnect',
                    onClick: () => {
                      console.log('This should change state');
                    },
                    leadingIcon: 'reset',
                  }}
                  title='Make sure the sensors are flashing at the same time..'
                />
              </IonRow>

              <AnimatedSensor animation='syncing' />

              <IonRow>
                <CalibrationFailed description='Movement detected, please re-calibrate' title='Calibration failed' />
              </IonRow>

              <RadioSelectButtonGroup onChange={() => console.log('onChange')} />
              <ActiveRecordingTimer startTime={Date.now() - 1000} />

              <BottomBar
                description='Ready to start recording! Select your exercise type and tap start recording'
                nextButton={{
                  label: 'Continue',
                  onClick: () => {
                    console.log('This should change state');
                  },
                  isDisabled: hubStatus !== 'connected',
                }}
                title='Recording'
              />

              <CalibrationCountdown isPlaying={isPlaying} />
              <Button onClick={() => setIsPlaying(true)}>Start countdown</Button>
            </IonGrid>
          </IonContent>
          <TagModal
            handleCancel={() => setShowTagModal(false)}
            handleClose={() => setShowTagModal(false)}
            isOpen={showTagModal}
          />
          <DebugOverlay initiallyVisible={false} />
        </IonPage>
      </SessionScreenContextProvider>
    </>
  );
};
